import React, { useState } from 'react';
import {
  IonContent,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
} from '@ionic/react';
import { LinkModel, LinkType, LinkDestination } from '../../../types/link';
import { LinkFormBase } from './LinkFormBase';
import styles from '../Dashboard.module.css';

interface CreateLinkFormProps {
  onSubmit: (link: Partial<LinkModel>, destination: Partial<LinkDestination>, file: File | null) => Promise<void>;
  onCancel: () => void;
}

export const CreateLinkForm: React.FC<CreateLinkFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<Partial<LinkModel>>({
    name: '',
    type: LinkType.Redirect,
    description: '',
    tags: {},
    categoryId: undefined,
    password: undefined,
    expirationDate: undefined,
  });
  const [destination, setDestination] = useState<Partial<LinkDestination>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Ensure name is set to URL if it's a redirect and name is empty
      const finalFormData = { ...formData };
      if (formData.type === LinkType.Redirect && destination.url && !formData.name) {
        finalFormData.name = destination.url;
      }
      
      await onSubmit(finalFormData, destination, selectedFile);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    if (loading) return false;
    if (!formData.name) return false;
    
    switch (formData.type) {
      case LinkType.File:
        return !!selectedFile;
      case LinkType.Redirect:
        return !!destination.url;
      case LinkType.Collection:
        return true; // Will need additional validation when implemented
      default:
        return false;
    }
  };

  return (
    <IonContent>
      <form onSubmit={handleSubmit}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Create New Link</h2>
          </div>
          
          <div className={styles.cardContent}>
            <LinkFormBase
              initialData={formData}
              initialDestination={destination}
              onFormDataChange={setFormData}
              onDestinationChange={setDestination}
              onFileChange={setSelectedFile}
              isEdit={false}
            />
          </div>

          <div className={styles.cardFooter}>
            <IonGrid>
              <IonRow>
                <IonCol>
                  <IonButton
                    expand="block"
                    type="submit"
                    disabled={!isFormValid()}
                  >
                    {loading ? 'Creating...' : 'Create Link'}
                  </IonButton>
                </IonCol>
                <IonCol>
                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={onCancel}
                  >
                    Cancel
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
        </div>
      </form>
    </IonContent>
  );
}; 